import { useParams } from "react-router-dom";
import { InfoIcon } from "@/assets/icons";
import styles from "../../styles.module.scss";
import { WarehouseScheduler } from "../../WarehouseScheduler";
import AutoManageSection from "./AutoManageSection";
import type { WarehouseAutoSavingsGraphItem } from "../../types";
import { Card, CardContent, Stack, Tabs, TabsList, TabsTrigger } from "@altimateai/lego";
import ChartTile from "./ChartTile";
import AutoTuneHistory from "./AutoTuneHistory";
import { useState } from "react";

interface CombinedAutoTuneSectionProps {
  data: {
    graph_data?: WarehouseAutoSavingsGraphItem[];
    top_info?: unknown;
  } | null;
  isLoading: boolean;
  isDynamicSchedule?: boolean;
  startDate: Date;
  endDate: Date;
  autoManageStatus: boolean;
  warehouseRK: string;
  estimatedSavings: number;
  aggregation: string;
  setAggregation: (value: string) => void;
  warehouse_size: string;
  warehouse_name: string;
  selectedView: string;
  setSelectedView: (value: string) => void;
  resizingEnabled?: boolean;
  isWarehouseSizingEnabled?: boolean;
}

const CombinedAutoTuneSection = ({
  data,
  isLoading,
  isDynamicSchedule = false,
  startDate,
  endDate,
  autoManageStatus,
  warehouseRK,
  estimatedSavings,
  aggregation,
  setAggregation,
  warehouse_size,
  warehouse_name,
  selectedView,
  setSelectedView,
  resizingEnabled = false,
  isWarehouseSizingEnabled = false,
}: CombinedAutoTuneSectionProps) => {
  const { instance_id } = useParams();
  const [graphType, setGraphType] = useState("decision");

  return (
    <div>
      <Stack justify={"between"} align={"center"} gap={"sm"} className="mb-4">
        <div style={{ minWidth: "fit-content" }}>
          <Tabs value={selectedView} onValueChange={setSelectedView}>
            <TabsList className="al-grid al-w-full al-grid-cols-2">
              <TabsTrigger value="performance">Performance</TabsTrigger>
              <TabsTrigger value="schedule">Schedule</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        {selectedView === "schedule" && !isDynamicSchedule && (
          <div className={styles.schedule_info}>
            <InfoIcon />
            Create and apply daily or weekly warehouse sizing schedule
          </div>
        )}
      </Stack>
      {selectedView === "performance" ? (
        <AutoManageSection
          data={data}
          isLoading={isLoading}
          startDate={startDate}
          endDate={endDate}
          autoManageStatus={autoManageStatus}
          warehouseRK={warehouseRK}
          estimatedSavings={estimatedSavings}
          aggregation={aggregation}
          setAggregation={setAggregation}
          resizingEnabled={resizingEnabled}
        />
      ) : (
        <WarehouseScheduler
          instance_id={instance_id || ""}
          warehouse_size={warehouse_size}
          warehouse_name={warehouse_name}
          isSchedulingEnabled={isWarehouseSizingEnabled}
          isDynamic={isDynamicSchedule}
          startDate={startDate}
          endDate={endDate}
        />
      )}

      {selectedView === "performance" && (
        <Stack direction="column">
          <Card>
            <CardContent className="al-pt-4">
              <ChartTile
                graphType={graphType}
                setGraphType={setGraphType}
                warehouse_rk={warehouseRK}
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent className="al-pt-4">
              <AutoTuneHistory warehouseRK={warehouseRK} />
            </CardContent>
          </Card>
        </Stack>
      )}
    </div>
  );
};

export default CombinedAutoTuneSection;
